'use client';

import { useState, useEffect } from 'react';
import { Server, Terminal, Download, Loader2 } from 'lucide-react';

interface DownloadLinks {
  version: string;
  publishedAt: string;
  downloads: {
    macosUniversal?: { url: string; size: number };
    macosArm64?: { url: string; size: number };
    macosX64?: { url: string; size: number };
    windowsSetup?: { url: string; size: number };
    linuxDeb?: { url: string; size: number };
    linuxAppImage?: { url: string; size: number };
    linuxRpm?: { url: string; size: number };
  };
  isPlaceholder?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

export default function DownloadSection() {
  const [releaseData, setReleaseData] = useState<DownloadLinks | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchRelease() {
      try {
        const response = await fetch('/api/systemlens/latest-release');
        if (!response.ok) {
          throw new Error('Failed to fetch release data');
        }
        const data = await response.json();
        setReleaseData(data);
      } catch (err) {
        console.error('Failed to fetch release:', err instanceof Error ? err.message : 'Unknown error');
        // Set placeholder data on error
        setReleaseData({
          version: '0.1.0',
          publishedAt: new Date().toISOString(),
          downloads: {},
          isPlaceholder: true,
        });
      } finally {
        setLoading(false);
      }
    }

    fetchRelease();
  }, []);

  const hasDownloads = releaseData && Object.keys(releaseData.downloads).length > 0;

  return (
    <section id="download" className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Download SystemLens
          </h2>
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-gray-300">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading release information...</span>
            </div>
          ) : (
            <p className="text-xl text-gray-300">
              {hasDownloads ? (
                <>Latest Release: v{releaseData.version} - Free & Open Source</>
              ) : (
                <>Coming Soon - Free & Open Source</>
              )}
            </p>
          )}
        </div>

        {!loading && (
          <>
            {hasDownloads ? (
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                {/* macOS */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Server className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">macOS</h3>
                    <p className="text-gray-300 text-sm">macOS 10.15 or later</p>
                  </div>
                  <div className="space-y-3">
                    {releaseData.downloads.macosUniversal ? (
                      <a
                        href={releaseData.downloads.macosUniversal.url}
                        className="block w-full bg-white text-gray-900 text-center py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                      >
                        <div>Universal Binary</div>
                        <div className="text-xs text-gray-600 mt-1">
                          {formatBytes(releaseData.downloads.macosUniversal.size)}
                        </div>
                      </a>
                    ) : (
                      <>
                        {releaseData.downloads.macosArm64 ? (
                          <a
                            href={releaseData.downloads.macosArm64.url}
                            className="block w-full bg-white text-gray-900 text-center py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                          >
                            <div>Apple Silicon (M1/M2/M3)</div>
                            <div className="text-xs text-gray-600 mt-1">
                              {formatBytes(releaseData.downloads.macosArm64.size)}
                            </div>
                          </a>
                        ) : (
                          <div className="block w-full bg-white/10 text-white/50 text-center py-3 rounded-lg font-semibold border border-white/10">
                            Apple Silicon - Coming Soon
                          </div>
                        )}
                        {releaseData.downloads.macosX64 ? (
                          <a
                            href={releaseData.downloads.macosX64.url}
                            className="block w-full bg-white/20 text-white text-center py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors border border-white/20"
                          >
                            <div>Intel (x64)</div>
                            <div className="text-xs text-gray-300 mt-1">
                              {formatBytes(releaseData.downloads.macosX64.size)}
                            </div>
                          </a>
                        ) : (
                          <div className="block w-full bg-white/10 text-white/50 text-center py-3 rounded-lg font-semibold border border-white/10">
                            Intel - Coming Soon
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Windows */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Server className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">Windows</h3>
                    <p className="text-gray-300 text-sm">Windows 10 or 11 (64-bit)</p>
                  </div>
                  <div className="space-y-3">
                    {releaseData.downloads.windowsSetup ? (
                      <a
                        href={releaseData.downloads.windowsSetup.url}
                        className="block w-full bg-white text-gray-900 text-center py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                      >
                        <div>Download Installer (.exe)</div>
                        <div className="text-xs text-gray-600 mt-1">
                          {formatBytes(releaseData.downloads.windowsSetup.size)}
                        </div>
                      </a>
                    ) : (
                      <div className="block w-full bg-white/10 text-white/50 text-center py-3 rounded-lg font-semibold border border-white/10">
                        Coming Soon
                      </div>
                    )}
                  </div>
                </div>

                {/* Linux */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Terminal className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">Linux</h3>
                    <p className="text-gray-300 text-sm">64-bit distributions</p>
                  </div>
                  <div className="space-y-3">
                    {releaseData.downloads.linuxDeb ? (
                      <a
                        href={releaseData.downloads.linuxDeb.url}
                        className="block w-full bg-white text-gray-900 text-center py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                      >
                        <div>Debian/Ubuntu (.deb)</div>
                        <div className="text-xs text-gray-600 mt-1">
                          {formatBytes(releaseData.downloads.linuxDeb.size)}
                        </div>
                      </a>
                    ) : (
                      <div className="block w-full bg-white/10 text-white/50 text-center py-3 rounded-lg font-semibold border border-white/10">
                        Debian/Ubuntu - Coming Soon
                      </div>
                    )}
                    {releaseData.downloads.linuxAppImage ? (
                      <a
                        href={releaseData.downloads.linuxAppImage.url}
                        className="block w-full bg-white/20 text-white text-center py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors border border-white/20"
                      >
                        <div>AppImage (Universal)</div>
                        <div className="text-xs text-gray-300 mt-1">
                          {formatBytes(releaseData.downloads.linuxAppImage.size)}
                        </div>
                      </a>
                    ) : (
                      <div className="block w-full bg-white/10 text-white/50 text-center py-3 rounded-lg font-semibold border border-white/10">
                        AppImage - Coming Soon
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 text-center">
                  <Download className="w-16 h-16 mx-auto mb-4 text-white/60" />
                  <h3 className="text-2xl font-bold mb-4">Builds Coming Soon</h3>
                  <p className="text-gray-300">
                    We're currently working on the build system. Check back soon for downloadable releases.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
